import {
  PRODUCT_IMAGE_MAX_SOURCE_BYTES,
  formatImageSize,
  validateImageFile,
} from "./imageUpload";

const PRODUCT_IMAGE_ACCEPT = "image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp";
const TARGET_BYTES = 2.5 * 1024 * 1024;
const MAX_DIMENSION = 1600;

function safeBaseName(name = "imagen") {
  return String(name || "imagen")
    .replace(/\.[^.]+$/, "")
    .replace(/[^a-zA-Z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80) || "imagen";
}

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(
        new Error(
          `No se pudo leer "${file.name}". Verificá que la imagen no esté dañada.`,
        ),
      );
    };
    image.src = url;
  });
}

function canvasToBlob(canvas, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("No se pudo comprimir la imagen."))),
      "image/webp",
      quality,
    );
  });
}

export async function optimizeProductImage(file) {
  validateImageFile(file, {
    accept: PRODUCT_IMAGE_ACCEPT,
    maxBytes: PRODUCT_IMAGE_MAX_SOURCE_BYTES,
    fieldLabel: "la publicación",
  });

  const image = await loadImage(file);
  const sourceWidth = Number(image.naturalWidth || image.width || 0);
  const sourceHeight = Number(image.naturalHeight || image.height || 0);
  if (!sourceWidth || !sourceHeight) {
    throw new Error(`"${file.name}" no contiene una imagen válida.`);
  }

  const scale = Math.min(1, MAX_DIMENSION / Math.max(sourceWidth, sourceHeight));
  let width = Math.max(1, Math.round(sourceWidth * scale));
  let height = Math.max(1, Math.round(sourceHeight * scale));
  const qualities = [0.86, 0.78, 0.68, 0.58];
  let lastBlob = null;

  for (let pass = 0; pass < 3; pass += 1) {
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d", { alpha: false });
    if (!context) throw new Error("El navegador no pudo procesar la imagen.");
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, width, height);
    context.drawImage(image, 0, 0, width, height);

    for (const quality of qualities) {
      lastBlob = await canvasToBlob(canvas, quality);
      if (lastBlob.size <= TARGET_BYTES) break;
    }

    if (lastBlob?.size <= TARGET_BYTES || Math.max(width, height) <= 900) break;
    width = Math.max(1, Math.round(width * 0.78));
    height = Math.max(1, Math.round(height * 0.78));
  }

  if (!lastBlob) throw new Error(`No se pudo preparar "${file.name}".`);
  return new File([lastBlob], `${safeBaseName(file.name)}.webp`, {
    type: "image/webp",
    lastModified: Date.now(),
  });
}

export async function optimizeProductImages(files = [], options = {}) {
  const list = Array.from(files || []);
  const total = list.length;
  const optimized = [];
  options.onProgress?.({ completed: 0, total, percent: total ? 0 : 100 });

  for (let index = 0; index < total; index += 1) {
    const file = list[index];
    optimized.push(await optimizeProductImage(file));
    const completed = index + 1;
    options.onProgress?.({
      completed,
      total,
      percent: Math.round((completed / total) * 100),
      file,
    });
  }
  return optimized;
}

export { formatImageSize };
